import { z } from "zod";
import { blankToNull, httpUrlSchema } from "./validation.js";
/**
 * The About page: an admin writes titled sections and a contact block, and
 * every visitor reads them at /about.
 *
 * Pure schemas and helpers with no runtime dependency beyond zod, so the API
 * validates and the browser renders from one definition of what an About page
 * is — the same reason `brandRamp.ts` lives here.
 */
/** One titled block of prose. Both halves required — a section with no body is a stray heading. */
export const aboutSectionSchema = z.object({
    title: z.string().trim().min(1).max(80),
    body: z.string().trim().min(1).max(2500),
});
/**
 * How to reach the organization. Every field optional and independently
 * clearable; `null` means "not set", never "unset and broken".
 *
 * Phone is length-capped but never format-validated: youth-sports contacts
 * carry extensions, country codes, and "(call or text)". `telHref` handles
 * turning whatever was typed into something dialable.
 */
export const aboutContactSchema = z.object({
    email: blankToNull(z.string().trim().toLowerCase().email().max(320).nullable()).default(null),
    phone: blankToNull(z.string().trim().max(40).nullable()).default(null),
    address: blankToNull(z.string().trim().max(240).nullable()).default(null),
    mapsUrl: blankToNull(httpUrlSchema.nullable()).default(null),
    facebookUrl: blankToNull(httpUrlSchema.nullable()).default(null),
    instagramUrl: blankToNull(httpUrlSchema.nullable()).default(null),
    xUrl: blankToNull(httpUrlSchema.nullable()).default(null),
});
/** The section cap keeps the worst case near 20 KB on a payload every page fetches. */
export const MAX_ABOUT_SECTIONS = 8;
export const aboutSchema = z.object({
    sections: z.array(aboutSectionSchema).max(MAX_ABOUT_SECTIONS).default([]),
    contact: aboutContactSchema.default(() => aboutContactSchema.parse({})),
});
/**
 * "No About page yet." A factory rather than a frozen constant: it seeds both
 * `DEFAULT_SITE_SETTINGS` and the admin form's draft state, and one shared
 * instance would let an edit in the browser mutate the server's default.
 */
export const emptyAbout = () => ({
    sections: [],
    contact: {
        email: null,
        phone: null,
        address: null,
        mapsUrl: null,
        facebookUrl: null,
        instagramUrl: null,
        xUrl: null,
    },
});
/**
 * Is there an About page worth linking to? The nav link, the footer link, and
 * the page's own empty state all ask this, and they must never disagree —
 * a link that survives its content is a link to a blank page.
 *
 * Takes `undefined` because the client's `SiteSettings.about` is optional: a
 * settings record written before this field existed has no value at all.
 */
export function hasAboutContent(about) {
    if (!about)
        return false;
    if (about.sections.length > 0)
        return true;
    return Object.values(about.contact).some((v) => typeof v === "string" && v.trim() !== "");
}
/**
 * A `tel:` link from whatever an admin typed. Cut at the first letter before
 * stripping punctuation: "555-123-4567 ext. 12" would otherwise dial
 * 555123456712, a number that does not exist.
 */
export function telHref(phone) {
    const dialable = phone.split(/[a-z]/i)[0] ?? "";
    return `tel:${dialable.replace(/[^\d+]/g, "")}`;
}
//# sourceMappingURL=about.js.map